import { CancelTokenSource } from "axios";

import { API } from "utils/api-config";

import { defaultErrorResponse } from "api/defaults";

import { Tenant } from "store/environments/environment-types";
import { Studio } from "store/environments/tenants/studio/studio-types";
import { StudioWorkflow, FailureAction } from "store/workflows/workflows-types";

export const loadStudio = async (
    environmentId: string,
    tenantId: string,
    cancelTokenSource?: CancelTokenSource
): Promise<[Tenant<Studio> | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.get(
        `environments/${environmentId}/tenants/${tenantId}/studio`,
        {
            cancelToken: cancelTokenSource?.token,
        }
    );

    if (!response) {
        return defaultErrorResponse;
    }

    return [response.data, response.status];
};

export const createTenant = async (
    environmentId: string,
    tenantId: string,
    payload: Tenant<Studio>,
    cancelTokenSource?: CancelTokenSource
): Promise<[StudioWorkflow | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.post(
        `environments/${environmentId}/tenants/${tenantId}/studio/tenants`,
        payload,
        {
            cancelToken: cancelTokenSource?.token,
        }
    );

    if (!response) {
        return defaultErrorResponse;
    }

    return [response.data, response.status];
};

export const rotateClientSecret = async (
    environmentId: string,
    tenantId: string,
    payload: Tenant<Studio>,
    cancelTokenSource?: CancelTokenSource
): Promise<[StudioWorkflow | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.post(
        `environments/${environmentId}/tenants/${tenantId}/studio/client-secret/rotate`,
        payload,
        {
            cancelToken: cancelTokenSource?.token,
        }
    );

    if (!response) {
        return defaultErrorResponse;
    }

    return [response.data, response.status];
};

export const loadStudioWorkflows = async (
    environmentId: string,
    tenantId: string,
    cancelTokenSource?: CancelTokenSource
): Promise<[StudioWorkflow[] | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.get(
        `environments/${environmentId}/tenants/${tenantId}/studio/workflows`,
        {
            cancelToken: cancelTokenSource?.token,
        }
    );

    if (!response) {
        return defaultErrorResponse;
    }

    return [response.data, response.status];
};

export const terminateStudioWorkflow = async (
    environmentId: string,
    tenantId: string,
    workflowId: string,
    cancelTokenSource?: CancelTokenSource
): Promise<[string | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.post(
        `environments/${environmentId}/tenants/${tenantId}/studio/workflows/${workflowId}/terminate`,
        undefined,
        {
            cancelToken: cancelTokenSource?.token,
        }
    );

    if (!response) {
        return defaultErrorResponse;
    }

    return [response.data, response.status];
};

export const resolveStudioWorkflow = async (
    environmentId: string,
    tenantId: string,
    workflowId: string,
    payload: FailureAction,
    cancelTokenSource?: CancelTokenSource
): Promise<[string | null, number]> => {
    if (!environmentId || !tenantId) {
        return defaultErrorResponse;
    }

    const response = await API.post(
        `environments/${environmentId}/tenants/${tenantId}/studio/workflows/${workflowId}/resolve`,
        payload,
        {
            cancelToken: cancelTokenSource?.token,
        }
    );

    if (!response) {
        return defaultErrorResponse;
    }

    return [response.data, response.status];
};
